const NON_ROOM_EXACT = new Set([
  "Missing Wall",
  "Continued",
  "Unassigned",
  "--- MITIGATION ---",
  "--- MITIGATION --- --- MITIGATION ---",
  "climate cont. (Bid)",
  "Heavy",
]);

const NOISE_TOKENS = new Set([
  "Height:",
  "Height",
  "Flooring",
  "SY",
  "LF",
  "SF",
  "Ceil.",
  "Perimeter",
  "Floor",
  "Room",
]);

function stripMeasurementChunks(name) {
  return name
    .replace(/\b\d+(\.\d+)?\s*(SY|SF|LF)\b/gi, "")
    .replace(/\b\d+(\.\d+)?['"]\b/g, "")
    .replace(/\b\d+(\.\d+)?\s*\d*['"]\b/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function collapseRepeatedHalves(name) {
  const parts = name.trim().split(/\s+/);
  if (parts.length % 2 !== 0) return name;

  const half = parts.length / 2;
  const first = parts.slice(0, half).join(" ");
  const second = parts.slice(half).join(" ");
  if (first === second) return first;
  return name;
}

function collapseImmediateDuplicates(name) {
  const parts = name.trim().split(/\s+/);
  const out = [];
  for (const word of parts) {
    if (out[out.length - 1] === word) continue;
    out.push(word);
  }
  return out.join(" ");
}

function stripNoiseTokens(name) {
  const parts = name.trim().split(/\s+/);
  const filtered = parts.filter((word) => !NOISE_TOKENS.has(word));
  return (filtered.join(" ") || name).trim();
}

function cleanupRoomName(raw) {
  if (!raw) return "";

  let name = raw.trim();

  if (NON_ROOM_EXACT.has(name)) return "";

  name = collapseRepeatedHalves(name);
  name = stripMeasurementChunks(name);
  name = collapseImmediateDuplicates(name);
  name = stripNoiseTokens(name);

  return name.trim();
}

function isNoiseRoom(cleanName) {
  if (!cleanName) return true;

  const lower = cleanName.toLowerCase();

  if (NON_ROOM_EXACT.has(cleanName)) return true;
  if (lower === "missing wall") return true;
  if (lower === "continued") return true;
  if (lower.includes("continued -")) return true;

  if (["ceil", "perimeter", "floor", "height"].some((t) => lower.includes(t))) {
    if (
      !lower.includes("living") &&
      !lower.includes("bed") &&
      !lower.includes("bath") &&
      !lower.includes("closet") &&
      !lower.includes("hall") &&
      !lower.includes("kitchen") &&
      !lower.includes("laundry")
    ) {
      return true;
    }
  }

  return false;
}

function parseLineNoFromId(idValue) {
  if (idValue === null || idValue === undefined) return undefined;
  const id = String(idValue).trim();
  if (!id) return undefined;
  if (/^\d+$/.test(id)) return Number(id);
  const tailMatch = id.match(/(?:-|_)(\d+)\s*$/);
  if (!tailMatch) return undefined;
  const num = Number(tailMatch[1]);
  return Number.isFinite(num) ? num : undefined;
}

function pickLineNo(item) {
  const candidates = [
    item?.estimateLineNo,
    item?.lineNo,
    item?.line_no,
    item?.lineNumber,
    item?.line_number,
    item?.estimateLineNumber,
    item?.estimate_line_no,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  const idLineNo = parseLineNoFromId(item?.id);
  if (Number.isFinite(idLineNo)) return idLineNo;
  return undefined;
}

function orderRoomItems(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const entries = items.map((item, index) => ({
    item,
    index,
    lineNo: pickLineNo(item),
  }));
  entries.sort((a, b) => {
    const aHas = Number.isFinite(a.lineNo);
    const bHas = Number.isFinite(b.lineNo);
    if (aHas && bHas) return a.lineNo - b.lineNo || a.index - b.index;
    if (aHas) return -1;
    if (bHas) return 1;
    return a.index - b.index;
  });
  return entries.map((entry) => {
    if (Number.isFinite(entry.lineNo)) {
      const lineNo = entry.lineNo;
      if (entry.item?.estimateLineNo === lineNo) return entry.item;
      return { ...entry.item, estimateLineNo: lineNo };
    }
    return entry.item;
  });
}

function getRoomMinLineNo(items) {
  let min = Number.POSITIVE_INFINITY;
  for (const item of items || []) {
    const lineNo = pickLineNo(item);
    if (Number.isFinite(lineNo)) min = Math.min(min, lineNo);
  }
  return min;
}

// main export
export function normalizeRoomsFromLevels(levels = []) {
  const out = [];

  for (const level of levels || []) {
    for (const room of level.rooms || []) {
      const rawName = room.name || "";
      let cleanName = cleanupRoomName(rawName);

      if (isNoiseRoom(cleanName)) continue;
      if (!cleanName && rawName) cleanName = rawName;

      const orderedItems = orderRoomItems(room.items || []);
      out.push({
        rawName,
        name: cleanName,
        level: level.name ?? null,
        roomSubtotal: room.roomSubtotal ?? null,
        items: orderedItems,
      });
    }
  }

  return out
    .map((room, index) => ({
      room,
      index,
      minLineNo: getRoomMinLineNo(room.items),
    }))
    .sort((a, b) => {
      const aHas = Number.isFinite(a.minLineNo);
      const bHas = Number.isFinite(b.minLineNo);
      if (aHas && bHas) return a.minLineNo - b.minLineNo || a.index - b.index;
      if (aHas) return -1;
      if (bHas) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.room);
}
